import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import Login from "./components/Login";
import { dedupeFetch } from "./apiRequestCache";
import Dashboard from "./components/Dashbord/Dashboard";
import toast, { Toaster } from "react-hot-toast";
import "./App.css";
import AdminDashboard from "./components/AdminDashboard";
import CreateUser from "./components/Users/CreateUser";

// Memoized wrapper to prevent unnecessary re-renders
const DashboardWrapper = React.memo(
  ({
    notes,
    userid,
    userRole,
    workspaces,
    defaultUserWorkspaceID,
    defaultUserWorkspaceName,
    onUpdateDefaultWorkspace,
    refreshNotes,
    addSiteNote,
    updateNote,
    projects,
    updateProject,
    jobs,
    tasks,
    files,
    onUploadDocument,
    onDeleteDocument,
    fetchDocuments,
    onLogout,
    documentCounts,
    fetchProjectAndJobs,
    fetchNotes,
    workspaceRole,
    role,
  }) => {
    const { SitenoteId } = useParams();
    return (
      <Dashboard
        notes={notes}
        userid={userid}
        userRole={userRole}
        workspaces={workspaces}
        defaultUserWorkspaceID={defaultUserWorkspaceID}
        defaultUserWorkspaceName={defaultUserWorkspaceName}
        onUpdateDefaultWorkspace={onUpdateDefaultWorkspace}
        refreshNotes={refreshNotes}
        addSiteNote={addSiteNote}
        updateNote={updateNote}
        projects={projects}
        updateProject={updateProject}
        jobs={jobs}
        tasks={tasks}
        files={files}
        onUploadDocument={onUploadDocument}
        onDeleteDocument={onDeleteDocument}
        fetchDocuments={fetchDocuments}
        onLogout={onLogout}
        documentCounts={documentCounts}
        fetchProjectAndJobs={fetchProjectAndJobs}
        fetchNotes={fetchNotes}
        urlNoteIdFromRoute={SitenoteId ? parseInt(SitenoteId) : null}
        workspaceRole={workspaceRole}
      />
    );
  }
);

function App() {
  // ---------- State ----------
  const [notes, setNotes] = useState([]);
  const [userId, setUserId] = useState(0);
  const [defaultWorkspace, setDefaultWorkspace] = useState("");
  const [userDefaultWork, setUserDefaultWork] = useState("");
  const [projects, setProjects] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [documentCounts, setDocumentCounts] = useState({});
  const [workspaces, setWorkspaces] = useState([]);
  const [role, setRole] = useState("");
  const [workspaceRole, setWorkspaceRole] = useState("");

  // Refs to prevent stale closures and track mounted state
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  // Guards against duplicate in-flight GetUserById calls (e.g. React StrictMode
  // double-invoked mount effects and storage events). Collapses concurrent
  // requests for the same user into a single round trip.
  const userFetchInFlightRef = useRef(0);
  const pageSize = 25;
  const apiUrl = process.env.REACT_APP_API_BASE_URL
    ? `${process.env.REACT_APP_API_BASE_URL}/api`
    : "/api";

  // ---------- Memoized API calls (stable references) ----------
  const fetchNotes = useCallback(
    async (page = 1, pageSizeParam = pageSize) => {
      if (!userId) return { notes: [], hasMore: false, currentPage: page };
      try {
        const response = await fetch(
          `${apiUrl}/SiteNote/GetSiteNotes?pageNumber=${page}&pageSize=${pageSizeParam}&userId=${userId}`,
          { headers: { "Content-Type": "application/json; charset=utf-8" } }
        );
        const text = await response.text();
        if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
        const data = JSON.parse(text);
        return {
          notes: data.items || [],
          hasMore: (data.items?.length || 0) === pageSizeParam,
          currentPage: page,
        };
      } catch (error) {
        console.error("Error fetching notes:", error);
        throw error;
      }
    },
    [apiUrl, userId, pageSize]
  );

  const getUser = useCallback(async (id) => {
    if (!id) return;
    // Coalesce duplicate concurrent requests (StrictMode double effect, storage
    // events): only the first in-flight call performs the round trip.
    userFetchInFlightRef.current += 1;
    if (userFetchInFlightRef.current > 1) return;
    try {
      const data = await dedupeFetch(`${apiUrl}/UserManagement/GetUserById/${id}`, {
        headers: { "Content-Type": "application/json; charset=utf-8" },
      });
      const user = data.user;
      setDefaultWorkspace(user.defaultWorkspaceId || "");
      setRole(user.role || "");
      setUserDefaultWork(user.defaultWorkspaceName || "");
    } catch (error) {
      console.error("Error fetching user:", error);
    } finally {
      userFetchInFlightRef.current -= 1;
      if (isMountedRef.current) setLoading(false);
    }
  }, [apiUrl]);

  const fetchUserWorkspaceRole = useCallback(async () => {
    if (!userId || !defaultWorkspace) return;
    try {
      const response = await fetch(`${apiUrl}/UserWorkspace/GetWorkspacesByUserId/${userId}`, {
        headers: { "Content-Type": "application/json; charset=utf-8" },
      });
      if (response.ok) {
        const data = await response.json();
        const userWorkspaces = data.userWorkspaces || data || [];
        const workspace = userWorkspaces.find(
          (ws) => ws.workspaceID && ws.workspaceID.toString() === defaultWorkspace.toString()
        );
        setWorkspaceRole(workspace?.role || null);
      } else {
        console.error("API response not OK:", response.status);
      }
    } catch (error) {
      console.error("Error fetching workspace role:", error);
    }
  }, [apiUrl, userId, defaultWorkspace]);

  const fetchProjectsAndJobs = useCallback(async () => {
    try {
      const [projectsRes, jobsRes, taskRes] = await Promise.all([
        fetch(`${apiUrl}/Project/GetProjects`),
        fetch(`${apiUrl}/Job/GetJobs`),
        fetch(`${apiUrl}/JobTasks?page=1&pageSize=200000`),
      ]);
      //if (!projectsRes.ok) throw new Error(`Projects fetch failed: ${projectsRes.status}`);
      if (!jobsRes.ok) throw new Error(`Jobs fetch failed: ${jobsRes.status}`);
      if (!taskRes.ok) throw new Error(`Task fetch failed: ${taskRes.status}`);

      const projectsData = (await projectsRes.json()).projects || [];
      const jobsData = (await jobsRes.json()).jobs || [];
      const taskData = (await taskRes.json()).data || [];

      setProjects(
        projectsData.map((p) => ({
          id: p.id,
          name: p.name,
          workspaceId: p.workspaceId,
          status: p.status,
        }))
      );
      setJobs(
        jobsData.map((j) => ({
          id: j.id.toString(),
          projectId: j.projectId.toString(),
          name: j.name,
          status: j.status,
        }))
      );
      setTasks(
        taskData.map((t) => ({
          id: t.id,
          taskName: t.title,
          status: t.status,
        }))
      );
    } catch (error) {
      console.error("Error fetching projects and jobs:", error);
      throw error;
    }
  }, [apiUrl]);

  const fetchInitialData = useCallback(async () => {
    if (!userId) return;
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    setError(null);
    try {
      const notesResult = await fetchNotes(1, pageSize);
      if (signal.aborted) return;
      if (notesResult?.notes) setNotes(notesResult.notes);
      await fetchProjectsAndJobs();
    } catch (err) {
      if (!signal.aborted) {
        setError(err.message);
        console.error("Initial data loading error:", err);
      }
    } finally {
      if (!signal.aborted && isMountedRef.current) setLoading(false);
    }
  }, [userId, fetchNotes, fetchProjectsAndJobs, pageSize]);

  // ---------- Handlers (memoized) ----------
  const handleLogin = useCallback((user) => {
    initializeUser();
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("user");
    localStorage.removeItem("userToken");
    setIsAuthenticated(false);
    setNotes([]);
    setUserId(0);
    setUserDefaultWork("");
    setDefaultWorkspace("");
    setRole("");
    setWorkspaceRole("");
  }, []);

  const refreshNote = useCallback(async () => {
    try {
      const result = await fetchNotes(1, pageSize);
      if (result?.notes) setNotes(result.notes);
    } catch (error) {
      console.error("Error refreshing notes:", error);
    }
  }, [fetchNotes, pageSize]);

  const addSiteNote = useCallback(
    async (siteNoteData) => {
      try {
        const response = await fetch(`${apiUrl}/SiteNote/AddSiteNote`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(siteNoteData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to save note");
        }
        return await response.json();
      } catch (error) {
        console.error("API Error:", error);
        throw error;
      }
    },
    [apiUrl]
  );

  const updateNote = useCallback(
    async (id, updatedData) => {
      try {
        const response = await fetch(`${apiUrl}/SiteNote/UpdateSiteNote/${id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            note: updatedData.Note,
            date: updatedData.Date,
            taskId: updatedData.TaskId,
            userId: updatedData.UserId,
          }),
        });
        if (!response.ok) {
          let errorMessage = `Server error: ${response.status} ${response.statusText}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch (e) {}
          throw new Error(errorMessage);
        }
        return await response.json();
      } catch (error) {
        console.error("Error updating note:", error);
        throw error;
      }
    },
    [apiUrl]
  );

  const updateDefaultWorkspace = useCallback((newDefId, newDefName) => {
    setDefaultWorkspace(newDefId);
    setUserDefaultWork(newDefName);
  }, []);

  const onUpdateProjectAndJob = useCallback(async () => {
    await fetchProjectsAndJobs();
  }, [fetchProjectsAndJobs]);

  const handleUploadDocument = useCallback(
    async (documentName, file, siteNoteId) => {
      if (!documentName?.trim()) throw new Error("Document name is required.");
      if (!file) throw new Error("File is required.");
      const user = JSON.parse(localStorage.getItem("user"));
      const userIdVal = user?.id || 1;
      const formData = new FormData();
      formData.append("Name", documentName);
      formData.append("File", file);
      formData.append("SiteNoteId", siteNoteId);
      formData.append("UserId", userIdVal);
      try {
        const response = await fetch(`${apiUrl}/Documents/AddDocument`, {
          method: "POST",
          body: formData,
        });
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
        }
        const result = await response.json();
        return {
          id: result.document.Id,
          name: result.document.Name,
          fileName: result.document.FileName,
        };
      } catch (error) {
        console.error("Failed to upload document:", error);
        throw error;
      }
    },
    [apiUrl]
  );

  const handleDeleteDocument = useCallback((docId, noteId) => {
    console.warn(
      "WARNING: Your C# API does not have a 'DeleteDocument' endpoint. This action will only remove the document from the frontend's local state."
    );
    setFiles((prevFiles) => ({
      ...prevFiles,
      [noteId]: prevFiles[noteId]?.filter((doc) => doc.id !== docId),
    }));
  }, []);

  const fetchDocumentsByReference = useCallback(
    async (noteId) => {
      try {
        const response = await fetch(`${apiUrl}/Documents/?siteNoteId=${noteId}`, {
          headers: { accept: "application/json; charset=utf-8" },
          credentials: "include",
        });
        if (!response.ok) throw new Error(`Failed to fetch documents: ${response.status}`);
        return await response.json();
      } catch (error) {
        console.error("Error fetching documents:", error);
        throw error;
      }
    },
    [apiUrl]
  );

  const updateProject = useCallback(
    async (projectData) => {
      try {
        const response = await fetch(`${apiUrl}/Sitebook/UpdateProject`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ Id: projectData.id, Name: projectData.name }),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to update project");
        }
        await fetchProjectsAndJobs();
        return await response.json();
      } catch (error) {
        console.error("Error updating project:", error);
        throw error;
      }
    },
    [apiUrl, fetchProjectsAndJobs]
  );

  // ---------- Initialization ----------
  const initializeUser = useCallback(() => {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        setUserId(parseInt(user.id));
        setIsAuthenticated(true);
        getUser(user.id);
      }
    } catch (error) {
      console.error("Error parsing user:", error);
    }
  }, [getUser]);

  // ---------- Effects ----------
  // Check authentication on mount and listen for storage changes
  useEffect(() => {
    const checkAuthState = () => {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        setUserId(parseInt(user.id));
        setIsAuthenticated(true);
        getUser(user.id);
      } else {
        setIsAuthenticated(false);
        setUserId(0);
      }
    };
    checkAuthState();
    window.addEventListener("storage", checkAuthState);
    return () => {
      window.removeEventListener("storage", checkAuthState);
    };
  }, [getUser]);

  // Fetch initial data when userId changes
  useEffect(() => {
    if (userId) {
      fetchInitialData();
    }
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [userId, fetchInitialData]);

  // Fetch workspace role when userId and defaultWorkspace are available
  useEffect(() => {
    if (userId && defaultWorkspace) {
      fetchUserWorkspaceRole();
    }
  }, [userId, defaultWorkspace, fetchUserWorkspaceRole]);

  // Cleanup mounted flag on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  // Memoize the DashboardWrapper props to avoid unnecessary re-renders
  const dashboardWrapperProps = useMemo(
    () => ({
      notes,
      userid: userId,
      userRole: role,
      workspaces,
      defaultUserWorkspaceID: defaultWorkspace,
      defaultUserWorkspaceName: userDefaultWork,
      onUpdateDefaultWorkspace: updateDefaultWorkspace,
      refreshNotes: refreshNote,
      addSiteNote,
      updateNote,
      projects,
      updateProject,
      jobs,
      tasks,
      files,
      onUploadDocument: handleUploadDocument,
      onDeleteDocument: handleDeleteDocument,
      fetchDocuments: fetchDocumentsByReference,
      onLogout: handleLogout,
      documentCounts,
      fetchProjectAndJobs: onUpdateProjectAndJob,
      fetchNotes,
      workspaceRole,
    }),
    [
      notes,
      userId,
      role,
      workspaces,
      defaultWorkspace,
      userDefaultWork,
      updateDefaultWorkspace,
      refreshNote,
      addSiteNote,
      updateNote,
      projects,
      updateProject,
      jobs,
      tasks,
      files,
      handleUploadDocument,
      handleDeleteDocument,
      fetchDocumentsByReference,
      handleLogout,
      documentCounts,
      onUpdateProjectAndJob,
      fetchNotes,
      workspaceRole,
    ]
  );

  // ---------- Render ----------
  return (
    <Router>
      <div className="app">
        {loading && isAuthenticated && (
          <div className="loading-container">
            <div className="loading-content">
              <div className="spinner"></div>
              <p>Loading data...</p>
            </div>
          </div>
        )}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes fadeIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
          .loading-container {
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: rgba(255, 255, 255, 0.95);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 2000;
            animation: fadeIn 0.5s ease-in forwards;
            transition: opacity 0.5s ease-out;
          }
          .loading-container.hide {
            opacity: 0;
            pointer-events: none;
          }
          .loading-container .spinner {
            width: 48px;
            height: 48px;
            animation: spin 1s linear infinite;
            margin-bottom: 15px;
          }
          .loading-container p {
            margin: 0;
            color: #1a1a1a;
          }
          .loading-content {
            background: rgba(255, 255, 255, 0.95);
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 15px;
          }
        `}</style>
        <Routes>
          <Route path="/admin-dashboard" element={<AdminDashboard userId={userId} />} />
          <Route
            path="/login"
            element={
              !isAuthenticated ? (
                <Login onLogin={handleLogin} />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/dashboard/:SitenoteId?"
            element={
              isAuthenticated ? (
                error ? (
                  <div className="error-container">
                    <p>Error: {error}</p>
                    <button onClick={fetchInitialData}>Retry</button>
                  </div>
                ) : (
                  <DashboardWrapper {...dashboardWrapperProps} />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/users/user-management"
            element={<CreateUser onLogin={handleLogin} />}
          />
          <Route
            path="/"
            element={
              <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
            }
          />
        </Routes>
      </div>
      <Toaster
        position="bottom-center"
        style={{
          width: "18rem",
          padding: "0.7rem",
          background: "rgba(175, 75, 62, 0.1)",
          borderRadius: "3rem",
          transition: "all 0.2s",
          opacity: toast.visible ? 0.6 : 0,
        }}
      />
    </Router>
  );
}

export default App;